import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Search, ClipboardList, Trash2, Car } from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const DOT = { good: "bg-emerald-400", attention: "bg-amber-400", critical: "bg-rose-400" };

export default function Reports() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/reports"); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async () => {
    if (!toDelete) return;
    try { await api.delete(`/reports/${toDelete.id}`); setItems((x) => x.filter((r) => r.id !== toDelete.id)); toast.success("Report deleted"); }
    catch { toast.error("Failed to delete"); }
    finally { setToDelete(null); }
  };

  const filtered = items.filter((r) => !q || (r.customer_name || "").toLowerCase().includes(q.toLowerCase()) || (r.number || "").toLowerCase().includes(q.toLowerCase()));

  const counts = (r) => {
    const c = { good: 0, attention: 0, critical: 0 };
    (r.items || []).forEach((i) => { if (c[i.status] !== undefined) c[i.status]++; });
    return c;
  };

  return (
    <Layout
      title="Vehicle Health Reports"
      action={<Button data-testid="new-report-button" onClick={() => navigate("/reports/new")} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"><Plus className="h-4 w-4 mr-1.5" /> New Report</Button>}
    >
      <div className="space-y-4 animate-fade-up">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="report-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reports" className="pl-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-16 text-center">
            <ClipboardList className="h-10 w-10 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-400 mb-4">No health reports yet.</p>
            <Button onClick={() => navigate("/reports/new")} data-testid="empty-new-report" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Create your first report</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((r) => {
              const c = counts(r);
              const vehicle = [r.vehicle_year, r.vehicle_make, r.vehicle_model].filter(Boolean).join(" ");
              return (
                <div key={r.id} data-testid={`report-card-${r.id}`}
                  onClick={() => navigate(`/reports/${r.id}`)}
                  className="rounded-xl border border-slate-800 bg-slate-900 p-5 cursor-pointer transition-transform duration-200 hover:-translate-y-[2px]">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-slate-500">{r.number}</p>
                      <p className="font-semibold text-slate-100 truncate mt-0.5">{r.customer_name}</p>
                    </div>
                    <Button variant="ghost" size="icon" data-testid={`delete-report-${r.id}`} onClick={(e) => { e.stopPropagation(); setToDelete(r); }} className="h-8 w-8 text-slate-500 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-sm text-slate-400 flex items-center gap-2 mt-2"><Car className="h-3.5 w-3.5 text-slate-600" /> {vehicle || "No vehicle"}{r.mileage ? ` · ${r.mileage} mi` : ""}</p>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-800 text-xs">
                    {["good", "attention", "critical"].map((s) => (
                      <span key={s} className="flex items-center gap-1.5 text-slate-400">
                        <span className={`h-2.5 w-2.5 rounded-full ${DOT[s]}`} /> {c[s]}
                      </span>
                    ))}
                    <span className="ml-auto text-slate-600">{fmtDate(r.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Delete report?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">{toDelete?.number} will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-report" onClick={remove} className="bg-rose-600 hover:bg-rose-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
